import * as React from 'react'

import { Table } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimesheetEditTimesheetRequestFragment,
  TimesheetEstimatedTimeFragment,
  TimesheetPageEmployeesSettingFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import type moment from 'moment'

import ShiftPanelTableBody from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody'
import ShiftPanelTableHead from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableHead'
import type Store from 'modules/attendance/components/ClockIn/Store'
import { AttendanceShiftLocationNodes } from 'modules/attendance/hooks/shift_locations/useFetchShiftLocations'
import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import { Inconsistencies } from 'modules/attendance/hooks/useShiftInconsistencies'
import { ValidationError } from 'modules/attendance/lib/validateShiftBreakDurationRules'
import type CalendarDay from 'modules/attendance/store/models/CalendarDay'
import { useEmployeeProjectsTracking } from 'modules/project_management/contexts/EmployeeProjectsTrackingContext'

import { getColSpan, shouldHideLocationColumn } from './utils'

interface RowPanelProps {
  store: Store
  open: boolean
  date: moment.Moment
  calendarDay: CalendarDay
  overtimeRequestMinutes: number
  allBreaks: BreakConfigurationFragment[]
  hasEditTimesheetAuthorization: boolean
  dayBreaks: AttendanceBreakConfigurations[]
  shiftLocations?: AttendanceShiftLocationNodes
  restTimeInconsistencies?: ValidationError[]
  attendanceSetting: TimesheetPageEmployeesSettingFragment
  setBackground: (
    calendarDay: CalendarDay,
    isTab?: boolean
  ) => 'champagne100' | 'grey100' | 'grey000White'
  editTimesheetRequests: TimesheetEditTimesheetRequestFragment[]
  showStartsNextDayCheckbox: boolean
  estimatedTime: TimesheetEstimatedTimeFragment
  trackedMinutes: number
  shifts: TimesheetPageShiftFragment[]
  inconsistencies?: Inconsistencies
  hasCorrectiveBreaks: boolean
  hasExtraHourManagementV2: boolean
  showExtraHoursColumn: boolean
}

const RowPanel = ({
  open,
  date,
  store,
  dayBreaks,
  allBreaks,
  calendarDay,
  setBackground,
  shiftLocations,
  attendanceSetting,
  overtimeRequestMinutes,
  restTimeInconsistencies,
  hasEditTimesheetAuthorization,
  editTimesheetRequests,
  showStartsNextDayCheckbox,
  estimatedTime,
  trackedMinutes,
  shifts,
  inconsistencies,
  hasCorrectiveBreaks,
  hasExtraHourManagementV2,
  showExtraHoursColumn,
}: RowPanelProps) => {
  const projectsData = useEmployeeProjectsTracking()

  if (!open) return null

  const rowBackgroundColor = setBackground(calendarDay, true)
  const hideLocationColumn = shouldHideLocationColumn(attendanceSetting)
  const hasProjects = !!projectsData?.queryData.hasProjectsToTrack
  const colSpan = getColSpan(
    hasProjects,
    hideLocationColumn,
    hasCorrectiveBreaks,
    hasExtraHourManagementV2,
    showExtraHoursColumn
  )

  return (
    <Table.Row>
      <Table.Cell wide colSpan={colSpan} padding="s0">
        <Table
          autoWidth
          noRadius
          style={{
            border: 'none',
          }}
        >
          <ShiftPanelTableHead
            hasProjects={hasProjects}
            rowBackgroundColor={rowBackgroundColor}
            hideLocationColumn={hideLocationColumn}
            hasEditTimesheetAuthorization={hasEditTimesheetAuthorization}
          />
          <ShiftPanelTableBody
            date={date}
            store={store}
            colSpan={colSpan}
            dayBreaks={dayBreaks}
            allBreaks={allBreaks}
            hasProjects={hasProjects}
            projectsData={projectsData}
            background={rowBackgroundColor}
            shiftLocations={shiftLocations}
            attendanceSetting={attendanceSetting}
            overtimeRequestMinutes={overtimeRequestMinutes}
            restTimeInconsistencies={restTimeInconsistencies}
            editTimesheetRequests={editTimesheetRequests}
            showStartsNextDayCheckbox={showStartsNextDayCheckbox}
            estimatedTime={estimatedTime}
            trackedMinutes={trackedMinutes}
            shifts={shifts}
            inconsistencies={inconsistencies}
          />
        </Table>
      </Table.Cell>
    </Table.Row>
  )
}

export default RowPanel
